/**
 * Manual object-scope editor for a Cognara linkage.
 *
 * Three ways to pick geometry, because no single one covers real plant models:
 *  · 3D branches  — walk the model hierarchy and tick branches
 *  · Properties   — require a property value (PDMS Attribute.Function = Civils …)
 *  · Viewer pick  — adopt whatever is currently selected in the 3D canvas
 *
 * Every route resolves through the same server-side expansion the engine's own
 * rules use, so a hand-built selection means exactly what a proposed one means.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ResizablePanelGroup, ResizablePanel, ResizableHandle,
} from '@/components/ui/resizable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ModelObjectTree } from './ModelObjectTree';
import {
  Search, Loader2, MousePointerClick, Crosshair, Plus, X, Check, Layers, Tag,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function ObjectScopeEditor({ open, onOpenChange, projectId, modelId, proposal,
                                    viewerRef, onSave }) {
  const base = `${API}/api/v1/projects/${projectId}/4d/models/${modelId}/cognara`;

  const [branches, setBranches] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const [propSearch, setPropSearch] = useState('');
  const [pickedBranches, setPickedBranches] = useState(() => new Set());
  const [nameContains, setNameContains] = useState('');
  const [propKey, setPropKey] = useState('');
  const [propValues, setPropValues] = useState(() => new Set());
  const [leavesOnly, setLeavesOnly] = useState(true);
  const [includeDescendants, setIncludeDescendants] = useState(true);
  const [searching, setSearching] = useState(false);
  const [matched, setMatched] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoAdopt, setAutoAdopt] = useState(true);

  // The working set — starts from whatever the proposal already holds.
  const [objects, setObjects] = useState([]);

  useEffect(() => {
    if (!open) return;
    setObjects(proposal?.proposedObjects || []);
    setPickedBranches(new Set(proposal?.rule?.branchPaths || []));
    setNameContains((proposal?.rule?.nameContains || []).join(', '));
    setPropKey(proposal?.rule?.propertyKey || '');
    setPropValues(new Set(proposal?.rule?.propertyValues || []));
    setLeavesOnly(proposal?.rule?.leavesOnly !== false);
    setMatched(null);
  }, [open, proposal]);

  useEffect(() => {
    if (!open || branches.length || properties.length) return;
    setLoadingMeta(true);
    (async () => {
      try {
        const [br, pr] = await Promise.all([
          authFetch(`${base}/objects/branches`),
          authFetch(`${base}/objects/properties`),
        ]);
        const b = await br.json().catch(() => ({}));
        const p = await pr.json().catch(() => ({}));
        if (br.ok) setBranches(b.branches || []);
        if (pr.ok) setProperties(p.properties || []);
      } catch { toast.error('Could not read the model hierarchy'); }
      setLoadingMeta(false);
    })();
  }, [open, base, branches.length, properties.length]);

  const toggleBranch = (path) => {
    setPickedBranches(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const toggleValue = (v) => {
    setPropValues(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const runSearch = useCallback(async (mode) => {
    setSearching(true);
    try {
      const r = await authFetch(`${base}/objects/search`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          branchPaths: [...pickedBranches],
          nameContains: nameContains.split(',').map(s => s.trim()).filter(Boolean),
          propertyKey: propKey,
          propertyValues: [...propValues],
          leavesOnly,
          includeDescendants,
          limit: 1500,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Search failed'); setSearching(false); return; }
      setMatched(d);
      if (mode === 'REPLACE') {
        setObjects(d.objects || []);
        toast.success(`${d.matched} object(s) matched — replacing the selection`);
      } else {
        setObjects(prev => {
          const have = new Set(prev.map(o => o.dbId));
          const added = (d.objects || []).filter(o => !have.has(o.dbId));
          toast.success(`${added.length} object(s) added`);
          return [...prev, ...added];
        });
      }
      if (d.truncated) toast.info('Showing the first 1500 matches — narrow the filter for the rest.');
    } catch { toast.error('Search failed'); }
    setSearching(false);
  }, [base, pickedBranches, nameContains, propKey, propValues, leavesOnly, includeDescendants]);

  const adoptIds = useCallback(async (ids, { quiet = false } = {}) => {
    if (!ids?.length) return;
    try {
      const r = await authFetch(`${base}/objects/by-ids?dbIds=${ids.join(',')}`);
      const d = await r.json().catch(() => ({}));
      const picked = d.objects || ids.map(dbId => ({ dbId, objectName: '', objectPath: '' }));
      setObjects(prev => {
        const have = new Set(prev.map(o => o.dbId));
        const added = picked.filter(o => !have.has(o.dbId));
        if (added.length) toast.success(`${added.length} object(s) adopted from the 3D model`);
        else if (!quiet) toast.info('Those objects are already linked.');
        return added.length ? [...prev, ...added] : prev;
      });
    } catch { if (!quiet) toast.error('Could not read the 3D selection'); }
  }, [base]);

  const adoptViewerSelection = async () => {
    const ids = viewerRef?.current?.getSelection?.() || [];
    if (!ids.length) {
      toast.info('Nothing is selected in the 3D model — click objects in the canvas first.');
      return;
    }
    await adoptIds(ids);
  };

  // Clicking geometry in the live canvas links it straight away — the user
  // shouldn't have to pick in 3D and then press a second button.
  useEffect(() => {
    if (!open || !autoAdopt) return;
    const unsub = viewerRef?.current?.subscribeSelection?.(ids => adoptIds(ids, { quiet: true }));
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [open, autoAdopt, viewerRef, adoptIds]);

  const previewInViewer = () => {
    const ids = objects.map(o => o.dbId).filter(v => v != null);
    if (!ids.length) { viewerRef?.current?.clearHotspot?.(); return; }
    const ok = viewerRef?.current?.applyHotspot?.(ids.map(dbId => ({ dbId, color: '#8B5CF6' })));
    if (ok === false) toast.info('Load the 3D model to preview this selection.');
  };

  const save = async () => {
    setSaving(true);
    await onSave(objects.map(o => o.dbId));
    setSaving(false);
    onOpenChange(false);
  };

  const visibleBranches = useMemo(() => {
    const q = branchSearch.trim().toLowerCase();
    const rows = q ? branches.filter(b => b.branchPath.toLowerCase().includes(q)) : branches;
    return rows.slice(0, 300);
  }, [branches, branchSearch]);

  const pickedIdSet = useMemo(() => new Set(objects.map(o => o.dbId)), [objects]);

  const activeProp = properties.find(p => p.key === propKey);

  const visibleProperties = useMemo(() => {
    const q = propSearch.trim().toLowerCase();
    const rows = q ? properties.filter(p => p.key.toLowerCase().includes(q)) : properties;
    return rows.slice(0, 80);
  }, [properties, propSearch]);

  if (!open) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 z-50 w-[760px] max-w-[85vw] bg-background
                    border-l shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
         data-testid="object-scope-editor">
        <div className="px-4 py-3 border-b shrink-0">
          <div className="text-base font-semibold flex items-center gap-2">
            <Layers className="w-4 h-4 text-orange-500" />
            <span className="truncate">Edit linked objects · {proposal?.activityCode} {proposal?.activityName}</span>
            <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto shrink-0"
                    onClick={() => onOpenChange(false)} data-testid="scope-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            The 3D model stays live on the left — click geometry there or tick it in the tree, both
            feed the same linkage. Nothing is written until you save.
          </p>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Pickers */}
          <div className="w-[58%] border-r flex flex-col min-h-0">
            <Tabs defaultValue="branches" className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid grid-cols-2 h-8 rounded-none shrink-0">
                <TabsTrigger value="branches" className="text-xs" data-testid="scope-tab-branches">
                  Model tree &amp; 3D
                </TabsTrigger>
                <TabsTrigger value="properties" className="text-xs" data-testid="scope-tab-properties">
                  Properties
                </TabsTrigger>
              </TabsList>

              <TabsContent value="branches" className="flex-1 m-0 min-h-0 data-[state=active]:flex flex-col overflow-hidden">
                <div className="p-2 border-b shrink-0 flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                          onClick={adoptViewerSelection} data-testid="scope-adopt-selection">
                    <MousePointerClick className="w-3 h-3" /> Adopt 3D selection
                  </Button>
                  <label className="flex items-center gap-1.5 text-[10px] whitespace-nowrap cursor-pointer"
                         title="Link objects the moment you click them in the 3D canvas">
                    <Checkbox checked={autoAdopt} onCheckedChange={v => setAutoAdopt(!!v)}
                              data-testid="scope-auto-adopt" />
                    Live pick
                  </label>
                  <span className="text-[10px] text-muted-foreground truncate">
                    Click geometry in the model, or tick a branch below — both link the same objects.
                  </span>
                </div>
                <ScrollArea className="flex-1 h-full">
                  <ModelObjectTree projectId={projectId} modelId={modelId} viewerRef={viewerRef}
                                   pickedIds={pickedIdSet}
                                   onAdd={(objs) => setObjects(prev => {
                                     const have = new Set(prev.map(o => o.dbId));
                                     return [...prev, ...objs.filter(o => !have.has(o.dbId))];
                                   })}
                                   onRemove={(dbIds) => setObjects(prev => {
                                     const drop = new Set(Array.isArray(dbIds) ? dbIds : [dbIds]);
                                     return prev.filter(o => !drop.has(o.dbId));
                                   })} />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="properties" className="flex-1 m-0 min-h-0 data-[state=active]:flex flex-col overflow-hidden">
                <ResizablePanelGroup direction="vertical" className="flex-1 min-h-0">
                <ResizablePanel defaultSize={38} minSize={15} className="flex flex-col min-h-0">
                <div className="p-2 border-b flex-1 min-h-0 flex flex-col gap-1.5">
                  <Input value={propSearch} onChange={e => setPropSearch(e.target.value)}
                         placeholder="Search properties (e.g. Function, Discipline, Material)…"
                         className="h-7 text-xs shrink-0" data-testid="scope-prop-search" />
                  <ScrollArea className="flex-1 min-h-0 w-full">
                    <div className="flex flex-wrap gap-1 pr-2">
                      {visibleProperties.map(p => (
                        <Button key={p.key} size="sm"
                                variant={propKey === p.key ? 'secondary' : 'outline'}
                                className="h-6 px-2 text-[10px]"
                                onClick={() => { setPropKey(p.key === propKey ? '' : p.key); setPropValues(new Set()); }}
                                data-testid={`scope-propkey-${p.key}`}>
                          <Tag className="w-2.5 h-2.5 mr-1" />{p.key} ×{p.distinctValues}
                        </Button>
                      ))}
                      {!visibleProperties.length && (
                        <span className="text-[10px] text-muted-foreground px-1">No property matches that search.</span>
                      )}
                    </div>
                  </ScrollArea>
                </div>
                </ResizablePanel>
                <ResizableHandle withHandle data-testid="scope-prop-split" />
                <ResizablePanel defaultSize={62} minSize={20} className="flex flex-col min-h-0">
                <ScrollArea className="flex-1 h-full">
                  {!activeProp ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">
                      Pick a property above to filter by its values.
                    </div>
                  ) : activeProp.values.map(v => (
                    <label key={v.value}
                           className="flex items-center gap-2 px-2.5 py-1.5 border-b hover:bg-muted/40 cursor-pointer"
                           data-testid={`scope-propvalue-${v.value}`}>
                      <Checkbox checked={propValues.has(v.value)}
                                onCheckedChange={() => toggleValue(v.value)} />
                      <span className="text-[11px] truncate">{v.value}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground">{v.count} obj</span>
                    </label>
                  ))}
                </ScrollArea>
                </ResizablePanel>
                </ResizablePanelGroup>
              </TabsContent>

              </Tabs>

            <div className="border-t p-2 shrink-0 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={nameContains} onChange={e => setNameContains(e.target.value)}
                       placeholder="Name contains (comma separated, e.g. BASE, FND)"
                       className="h-7 text-xs" data-testid="scope-name-contains" />
                <label className="flex items-center gap-1.5 text-[10px] whitespace-nowrap">
                  <Checkbox checked={leavesOnly} onCheckedChange={v => setLeavesOnly(!!v)}
                            data-testid="scope-leaves-only" />
                  Geometry leaves only
                </label>
                <label className="flex items-center gap-1.5 text-[10px] whitespace-nowrap"
                       title="PDMS models tag the assembly, not the geometry — tick this to take everything underneath a match">
                  <Checkbox checked={includeDescendants} onCheckedChange={v => setIncludeDescendants(!!v)}
                            data-testid="scope-include-descendants" />
                  Include geometry underneath
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 flex-1"
                        onClick={() => runSearch('ADD')} disabled={searching}
                        data-testid="scope-search-add">
                  {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                  Add matches
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 flex-1"
                        onClick={() => runSearch('REPLACE')} disabled={searching}
                        data-testid="scope-search-replace">
                  Replace with matches
                </Button>
                {matched && (
                  <Badge variant="outline" className="text-[10px] h-6" data-testid="scope-match-count">
                    {matched.matched} matched
                  </Badge>
                )}
              </div>
              {/* Filters combine with AND, so a leftover branch pick silently
                  narrows a property search. Say so, and offer one click out. */}
              {(!!pickedBranches.size || !!propKey || !!nameContains.trim()) && (
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <span data-testid="scope-active-filters">
                    Matching: {pickedBranches.size ? `${pickedBranches.size} branch(es)` : 'any branch'}
                    {propKey ? ` + ${propKey}${propValues.size ? ` (${propValues.size} value${propValues.size > 1 ? 's' : ''})` : ''}` : ''}
                    {nameContains.trim() ? ` + name ~ ${nameContains.trim()}` : ''}
                  </span>
                  <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] ml-auto"
                          onClick={() => { setPickedBranches(new Set()); setPropKey(''); setPropValues(new Set()); setNameContains(''); setMatched(null); }}
                          data-testid="scope-clear-filters">
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Working set */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-3 py-2 border-b shrink-0 flex items-center gap-2">
              <span className="text-xs font-medium">Linked objects</span>
              <Badge variant="outline" className="text-[10px] h-5" data-testid="scope-selected-count">
                {objects.length}
              </Badge>
              <div className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
                        onClick={previewInViewer} data-testid="scope-preview-btn">
                  <Crosshair className="w-3 h-3" /> Preview in 3D
                </Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-rose-600"
                        onClick={() => setObjects([])} data-testid="scope-clear-btn">
                  Clear
                </Button>
              </div>
            </div>
            <ScrollArea className="flex-1">
              {!objects.length ? (
                <div className="p-6 text-center text-xs text-muted-foreground">
                  No objects linked yet — pick branches, filter by a property, or adopt your 3D selection.
                </div>
              ) : (
                <div className="divide-y">
                  {objects.slice(0, 600).map(o => (
                    <div key={o.dbId} className="flex items-center gap-2 px-2.5 py-1 hover:bg-muted/40"
                         data-testid={`scope-object-${o.dbId}`}>
                      <span className="text-[10px] font-mono text-muted-foreground w-12 shrink-0">#{o.dbId}</span>
                      <span className="text-[11px] truncate">{o.objectName || '(unnamed)'}</span>
                      <Button size="icon" variant="ghost" className="h-5 w-5 ml-auto shrink-0"
                              onClick={() => viewerRef?.current?.selectAndZoom?.(o.dbId)}
                              title="Zoom to object">
                        <Crosshair className="w-3 h-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-rose-600"
                              onClick={() => setObjects(prev => prev.filter(x => x.dbId !== o.dbId))}
                              data-testid={`scope-remove-${o.dbId}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {objects.length > 600 && (
                    <div className="px-2.5 py-2 text-[10px] text-muted-foreground">
                      …and {objects.length - 600} more (all of them will be saved).
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        <div className="border-t px-4 py-2.5 flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-muted-foreground">
            Saving replaces this activity's linkage with the {objects.length} object(s) listed.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" className="h-7 text-xs gap-1 bg-orange-600 hover:bg-orange-700"
                    onClick={save} disabled={saving} data-testid="scope-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save linkage
            </Button>
          </div>
        </div>
    </div>
  );
}

export default ObjectScopeEditor;
